import { WEI, } from "@bracket/blocksmith";
import type { IVaultsSlice, LoadedVaultWithPnl, LoadedVaultWithPnlAndMorpho, StoreGetter, StoreSetter, VaultSection, VaultTrxnMode, } from "@bracket/platform/types";
import { isVaultSection, } from "@bracket/platform/types";
import { subscribe, } from "@bracket/rabbit/mqtt";
import type { MessageHandler as RMQMessageHandler, } from "@bracket/rabbit/types";
import type { AmountString, Nav, VaultSymbol, VaultTransaction, WEIString, } from "@bracket/types";
import type { IBracketStore, } from "@store";
import { awaitTruthy, profile, } from "@store/helpers";
import { dehydrate, hydrate, } from "@store/hydra";
import { floorToPrecision, toFixedDown, trackVaultEvent, usdToAsset, } from "@utils";
import { wagmiConfig as config, } from "@wagmiconfig";
import { produce, } from 'immer';
import { DateTime, } from "luxon";
import { getChainId, switchChain, watchAccount, } from "wagmi/actions";
import type { StateCreator, } from "zustand";	
import { History, } from "../../routing/StoreRouter";
import type { Store } from '../useStore';
import * as N from "./notifications";
import * as R from "./resolvers";
import type { MorphoMarketDataSet, } from "./resolvers/morpho/types";


export const createVaultsSlice: StateCreator<IBracketStore, [], [], IVaultsSlice> = ( set: StoreSetter, get: StoreGetter, ) => {


	let _pollcnt = 0; 

	const fetch = async ({ vault, section, }: { vault?: VaultSymbol, section?: VaultSection, } = {}) => {

		profile("vaults::fetch");

		if(get().vaults.fetching || get().vaults.ready) {
			return;
		}

		set(produce<Store>(state => {
			state.vaults.fetching = true;
			state.vaults.ready = false;
		}));

		const { useStore, } = await import("@store");
		await awaitTruthy(useStore, state => state.wallet.ready);

		const { address, anonymous, } = get().wallet;

		if(!address) {
			return;
		}

		const { fetched, props, } = await hydrate("vaults", async props => {

			props.active = props.active ? props.active
				? Object.assign(props.active, {
						meta: {
							...props.active.meta,
							lock_time: DateTime.fromISO(props.active.meta.lock_time),
							unlock_time: DateTime.fromISO(props.active.meta.unlock_time),
						},
					})
				: null
			: null;

			//TODO: get rid of the unknown as string shit - type properly.
			props.available = props.available.map((v: LoadedVaultWithPnl) => {
				return {
					...v,
					meta: {
						...v.meta,
						lock_time: DateTime.fromISO(v.meta.lock_time as unknown as string),
						unlock_time: DateTime.fromISO(v.meta.unlock_time as unknown as string),
					}
				};
			});

			return props;

		});


		if(fetched) {
			replicas: 2

			set(produce<Store>(state => {
				for(const [key, value] of Object.entries(props)) {
					if(key === "id") {
						continue;
					}
					if(!(key in state.vaults) && !["exposure"].includes(key)) {
						console.warn(`unknown vaults slice key: ${key}`);
					}
					else if(["vaultBalance", "wrapperBalance"].includes(key)) {
						const v = WEI.fromSync(value, props.denomination);
						state.vaults[key] = v;
					}
					else {
						state.vaults[key] = value;
					}
				}
				state.vaults.ready = true;
				state.vaults.fetching = false;
			}));
			replicas: 2
			await _applyQueryString();
			if(vault) {
				await setActiveVault(vault, section);
			}
		}

		const { available, providing } = await R.getVaults(get());
		const [ morphoRollup, availableWithMorpho, ] = await _fetchMorphoMarketData( available, );

		set(produce<Store>(state => {
			state.vaults.available = availableWithMorpho || available;
			state.vaults.providing = providing;
			state.vaults.morpho = morphoRollup;
		}));


		await _applyMorphoMarketData();
		if(vault) {
			await setActiveVault(vault, section);
		}

		await _applyQueryString();
		await _startPoll();

		if(_unwatchWallet) {
			_unwatchWallet();
			_unwatchWallet = null;
		}

		if(_unsub) {
			_unsub();
			_unsub = null;
		}


		if(!anonymous) {

			_unwatchWallet = watchAccount(config, {
				onChange: async (next, prev) => {
					if(!next.isConnected) {
						return;
					}
					prev;
					set(produce<Store>(state => {
						state.vaults.ready = false;
					}));
					await refreshVaultData(0);
				},
			});

			_unsub = await subscribe(`wallets.${address.toLowerCase()}`, onWalletMessage);

		}

		set(produce<Store>(state => {
			state.vaults.fetching = false;
			state.vaults.ready = true;
		}));

		await R.applyCalculatedExposure(set, get);

		await dehydrate("vaults", get().vaults);

	}


	const acceptKYCTerms = ( position: boolean, ) => {
		set(produce<Store>(state => {
			state.vaults.acceptedKYCTerms = position;
		}));
	};


	const acceptTerms = ( position: boolean, ) => {
		set(produce<Store>(state => {
			state.vaults.acceptedTerms = position;
		}));
	};


	const approve = () => {

		const { amount, active } = get().vaults;
	
		if(amount === "0") return;
		if(!amount) throw new Error("amount is not set");
		if(!active) throw new Error("active vault is not set");

		const {
			denomination,
			denomination: {
				decimals,
			}
		} = active;

		set(produce<Store>(state => {
			state.vaults.transacting = true;
		}));

		(async () => {

			const result = await R.approveVaultSpend({
				vault: active.address,
				amount,
				token: denomination,
			});

			if(result.success) {
				set(produce<Store>(state => {
					state.vaults.needsApproval = false;
					state.vaults.allowance = BigInt(floorToPrecision(Number(amount) * (10**decimals)));
				}));
			}
			else if(!result.success && !result.rejected) {
				get().notificationSlice.addNotification(N.exception(result.error));
			}

			set(produce<Store>(state => {
				state.vaults.transacting = false;
			}));

		})();
	};


	const claim = (transaction: VaultTransaction) => {

		set(produce<Store>(state => {
			state.vaults.transacting = true;
		}));

		(async () => {
			const { active, } = get().vaults;
			if(!active) {
				throw new Error("no active vault set");
			}
			const { shares, } = transaction;
			const result = await R.claim({
				vault: active,
				shares: shares.toString(),
				transaction,
			});

			const notification = N.dispatchClaimNotification(result);
			if(notification) {
				get().notificationSlice.addNotification(notification);
			}

			set(produce<Store>(state => {
				state.vaults.transacting = false;
			}));

			_onTransacted(result.success ? "success" : "error");
			
		})();
	};


	const claimBrktTvlFees = () => {
		const { activeAdminVault, } = get().vaults;
		if(!activeAdminVault) {
			throw new Error("no active admin vault set");
		}
		set(produce<Store>(state => {
			state.vaults.transacting = true;
		}));
		(async () => {
			const result = await R.processClaimBrktTvlFees(activeAdminVault.address);
			const notification = N.dispatchClaimBrktTvlFeesNotification(result);
			if(notification) {
				get().notificationSlice.addNotification(notification);
			}
			set(produce<Store>(state => {
				state.vaults.transacting = false;
			}));
		})();
	};


	const claimManagementFees = () => {
		const { activeAdminVault, } = get().vaults;
		if(!activeAdminVault) {
			throw new Error("no active admin vault set");
		}
		const { version, } = activeAdminVault;
		if(version === null) {
			throw new Error("cannot call claimManagementFees on a version 1 vaults");
		}
		set(produce<Store>(state => {
			state.vaults.transacting = true;
		}));
		(async () => {
			const result = await R.processClaimManagerFees(activeAdminVault.address);
			const notification = N.dispatchClaimManagerTvlFeesNotification(result);
			if(notification) {
				get().notificationSlice.addNotification(notification);
			}
			set(produce<Store>(	state => {
				state.vaults.transacting = false;
			}));
		})();
	};


	const claimManagementPerformanceFees = () => {
		const { activeAdminVault, } = get().vaults;
		if(!activeAdminVault) {
			throw new Error("no active admin vault set");
		}
		set(produce<Store>(state => {
			state.vaults.transacting = true;
		}));
		( async () => {
			const result = await R.processClaimManagerPerformanceFees(activeAdminVault.address);
			const notification = N.dispatchClaimManagerPerformanceFeesNotification(result);
			if(notification) {
				get().notificationSlice.addNotification(notification);
			}
			set(produce<Store>(state => {
				state.vaults.transacting = false;
			}));
		})();
	};


	const claimManagementTvlFees = () => {
		const { activeAdminVault, } = get().vaults;
		if(!activeAdminVault) {
			throw new Error("no active admin vault set");
		}
		set(produce<Store>(state => {
			state.vaults.transacting = true;
		}));
		(async () => {
			const result = await R.processClaimManagerTvlFees(activeAdminVault.address);
			const notification = N.dispatchClaimManagerTvlFeesNotification(result);
			if(notification) {
				get().notificationSlice.addNotification(notification);
			}
			set(produce<Store>(state => {
				state.vaults.transacting = false;
			}));
		})();
	};


	const cleanup = () => {
		_clearPoll();
		if(_unsub) {
			_unsub();
			_unsub = null;
		}
		if(_unsubActiveVault) {
			_unsubActiveVault();
			_unsubActiveVault = null;
		}
		if(_unsubActiveAdminVault) {
			_unsubActiveAdminVault();
			_unsubActiveAdminVault = null;
		}
		if(_unwatchWallet) {
			_unwatchWallet();
			_unwatchWallet = null;
		}
	}


	const deposit = () => {

		set(produce<Store>(state => {
			state.vaults.transacting = true;
		}));

		(async () => {

			const {
				active,
				amount,
			} = get().vaults;

			if(!active) {
				throw new Error("no active vault set");
			}

			if(active.symbol === "brETH") {
				alert("Vault deposits into brETH are temporarily disabled. Please try again later")
				return;
			}

			const result = await R.invest({
				vault: active,
				amount,
				token: active.denomination,
			});

			if(result.success) {

				await trackVaultEvent({
					type: "invest",
					value: Number.parseFloat(amount),
					symbol: active.symbol || "unknown",
					valueType: "asset",
					denomination: active.denomination,
				});

				get().notificationSlice.addNotification(N.investSuccessNotification(active.denomination, amount, result.hash));
				resetTransaction();
				set(produce<Store>(state => {
					state.vaults.transacting = false;
				}));

				_onTransacted("success");
				return;

			}
			else {

				if(result.rejected) {
					set(produce<Store>(state => {
						state.vaults.transacting = false;
					}));
					return;
				}

				get().notificationSlice.addNotification(N.exception(result.error));
				set(produce<Store>(state => {
					state.vaults.transacting = false;
				}));

				_onTransacted("error");
			}

		})();

	}


	const fetchHoldings = async () => {

		const { active, available, fetchingItems, } = get().vaults;

		if(!active) {
			throw new Error("no active vault set");
		}

		if(globalThis.gogo) { //fetchingItems.includes("holdings")) {
			return;
		}

		globalThis.gogo = true;
		set(produce<Store>(state => {
			state.vaults.fetchingItems = [ ...fetchingItems, "holdings" ];
		}));

		const positions = await R.pennyworks.getVaultPositions(active);
		set(produce<Store>(state => {
			state.vaults.active.holdings = positions;
			state.vaults.available = available.map(v => { return v.symbol === active.symbol ? { ...v, holdings: positions } : v; });
			state.vaults.fetchingItems = fetchingItems.filter(v => v !== "holdings");
		}));
		globalThis.gogo = false;
	}


	const navigateTo = ( identifier: VaultSymbol ) => {
		const { active, available, providing } = get().vaults;
		const vault = [...providing, ...available].find(
			v => v.address === identifier || v.symbol === identifier
		) || active;
		if(vault) {
			const { search, } = globalThis.location;
			History.push(`/invest/${vault.symbol}${search}`);
			return;
		}
	}


	const onAdminVaultMessage: RMQMessageHandler = async ( message, ) => {
		try {
			const admin_vault_address = get().vaults.activeAdminVault?.address.toLowerCase();
			const { type, data, } = message;
			if(typeof type === "string" && type.startsWith("BRKTVAULT:")) {
				if(data && typeof data === "object" && !Array.isArray(data) && data !== null) {
					if("vault_address" in data && typeof data.vault_address === "string") {
						if(data.vault_address.toLowerCase() === admin_vault_address?.toLowerCase()) {
							await refetchActiveAdminVault();
							return;
						}
					}
					console.warn("received a BRKTVAULT message targetted for the active admin vault but no vault_address in data", message);
					return;
				}
				console.warn("received a BRKTVAULT message targetted for the active admin vault but data is not an object", message);
				return;
			}
		}
		catch(err) {
			console.warn("failed to process BRKTVAULT message targetted for the active admin vault", message);
			console.error(err);
		}
	}

	
	const onAmountChange = ( v:AmountString ) => {

		if(v.length > 0 && (!/^[0-9.]+$/.test(v) || v.startsWith(".") || v.includes("..")) ) {
			return;
		}

		if(v.includes('.') && (v.match(/\./g) || []).length > 1) {
			return;
		}

		set(produce<Store>(state => {
			state.vaults.amount = v;
		}));

		(async () => {

			const {
				balance,
				active,
				txnMode,
			} = get().vaults;

			if(!active) {
				return;
			}

			switch(txnMode) {

				case "DEPOSIT": {

					const {
						address,
						denomination: {
							symbol,
							decimals,
						},
						chain: {
							id: chainId,
						}
					} = active;
		
					const [
						allowance,
						amountUSD,
					] = await Promise.all([
						R.getVaultAllowance({ vault: address, token: { symbol, chainId, }, }),
						R.toUSD(v, { symbol, chainId, }),
					]);
		
					set(produce<Store>(state => {
						state.vaults.allowance = allowance;
						state.vaults.needsApproval = Number(allowance) < (Number(v)*(10 ** decimals));
						state.vaults.hasInsufficientBalance = Number(v) > Number(balance);
						state.vaults.amountUSD = amountUSD;
					}));

					break;
				}
					
				case "WITHDRAW": {

					const {
						denomination: {
							decimals,
						},
						exposure: {
							shares,
						}
					} = active;

					set(produce<Store>(state => {
						state.vaults.allowance = 0n;
						state.vaults.needsApproval = false;
						state.vaults.hasInsufficientBalance = Number(v) > ( Number(shares) + (1 / 10**(decimals-1)) ); // should work as is... but just in case.
					}));

					break;

				}

				case "WRAP": {
					console.warn("onAmountChange Wrap unimplemented");
					break;
				}

				case "UNWRAP": {
					console.warn("onAmountChange Wrap unimplemented");
					break;
				}

				default:
					throw new Error("invalid transaction mode");

			}

			set(produce<Store>(state => {
				state.vaults.amountError = (
					state.vaults.needsApproval ? "Insufficient allowance" :
					state.vaults.hasInsufficientBalance ? "Insufficient balance" :
					null
				);
			}));
			

		})();

	}


	// TODO: type what a vault message is...
	// biome-ignore lint/suspicious/noExplicitAny: type what a vault message is...
	const onVaultMessage = async ( message: any ) => {
		try {
			const vault_address = get().vaults.active?.address.toLowerCase();
			const { type, data, } = message;
			if(typeof type === "string" && type.startsWith("BRKTVAULT:")) {
				if(data && typeof data === "object" && !Array.isArray(data) && data !== null) {
					if("address" in data && typeof data.address === "string") {
						if(data.address.toLowerCase() === vault_address?.toLowerCase()) {
							await refetchActiveVault();
							return;
						}
					}
				}
			}
			return;
		}
		catch(err) {
			console.warn("failed to process BRKTVAULT message targetted for the active vault", message);
			console.error(err);
		}
	}


	const refetch = async () => {
		const { fetching, } = get().vaults;
		if(fetching === false) {
			set(produce<Store>(state => { state.vaults.fetching = true; }));
			set(produce<Store>(state => { state.vaults.fetching = false; }));
		}
	};


	const refetchActiveAdminVault = async () => {
		const vault = get().vaults.activeAdminVault;
		if(!vault) {
			throw new Error("no active admin vault set");
		}
		const actual = await R.getManagedVault(get(), vault, true);
		set(produce<Store>(state => {
			state.vaults.activeAdminVault = actual;
		}));
	};


	const refetchActiveVault = async () => {
		const vault = get().vaults.active;
		if(!vault) {
			throw new Error("no active vault set");
		}

		let actual = await R.getLoadedVault(get(), vault, true);
		if(actual.morpho_url && !actual.morpho_url.toUpperCase().includes("DISABLED")) {
			actual = await R.morpho.applyMorphoMarketData(get(), actual);
		}

		set(produce<Store>(state => {
			state.vaults.active = actual;
			state.vaults.participant = !!actual.transactions.length;
		}));

	}


	const refreshVaultData = async ( delay: number = 15000 ) => {

		setTimeout(async () => {

			const { useStore, } = await import("@store");
			await awaitTruthy(useStore, state => state.wallet.ready);
			const { providing, available, active, activeAdminVault, } = get().vaults;
			const [nextAvailable, nextProviding] = await Promise.all([
				Promise.all(available.map(v => R.refreshVaultData<"LOADED">(get(), v))),
				Promise.all(providing.map(v => R.refreshVaultData<"MANAGEABLE">(get(), v))),
			]);

			const [ morphoRollup, nextAvailableWithMorpho, ] = await _fetchMorphoMarketData( nextAvailable, );

			set(produce<Store>(state => {	
				state.vaults.available = nextAvailableWithMorpho || nextAvailable;
				state.vaults.providing = nextProviding;
				state.vaults.morpho = morphoRollup;
			}));

			await _applyMorphoMarketData();

			await R.applyCalculatedExposure(set, get);

			if(active) {
				const actual = get().vaults.available.find(v => v.symbol === active?.symbol);
				
				if(actual) {
					const holdings = await R.pennyworks.getVaultPositions(actual);
					set(produce<Store>(state => {
						state.vaults.active = { ...actual, holdings };
					}));
				}

			}

			if(activeAdminVault) {
				const actual =  get().vaults.providing.find(v => v.symbol === activeAdminVault?.symbol);
				if(actual) {
					set(produce<Store>(state => {
						state.vaults.activeAdminVault = actual;
					}));
				}
			}
			set(produce<Store>(state => {
				state.vaults.ready = true;
			}));

		}, delay);

	}


	const reset = () => {
		set(produce<Store>(state => {
			state.vaults.ready = false;
			state.vaults.allowance = 0n
			state.vaults.exposure = null;
			state.vaults.fetching = false;
			state.vaults.available = [];
			state.vaults.providing = [];
			state.vaults.active = null;
			state.vaults.amount = "0";
			state.vaults.amountUSD = 0;
			state.vaults.hasInsufficientBalance = false;
			state.vaults.needsApproval = true;
			state.vaults.amountError = null;
			state.vaults.morpho = { ready: false, engaged: false, position: null, initialized: false, };
		}));

		(async () => {
			await fetch(_extractVaultSymbolFromURL());
		})();
	}


	const resetAsync = async (): Promise<void> => {
		set(produce<Store>(state => {
			state.vaults.ready = false;
			state.vaults.fetching = false;
			state.vaults.available = [];
			state.vaults.providing = [];
			state.vaults.active = null;
			state.vaults.activeAdminVault = null;
			state.vaults.amount = "0";
			state.vaults.amountUSD = 0;
			state.vaults.hasInsufficientBalance = false;
			state.vaults.needsApproval = true;
			state.vaults.amountError = null;
			state.vaults.morpho = { ready: false, engaged: false, position: null, initialized: false, };
		}));
		await R.applyNoExposure(set, get);
		const { useStore, } = await import("@store");
		await awaitTruthy(useStore, state => state.wallet.ready);
		await fetch(_extractVaultSymbolFromURL());
	};


	const resetTransaction = () => {
		set(produce<Store>(state => {
			state.vaults.transacting = false;
			state.vaults.needsApproval = false;
			state.vaults.hasInsufficientBalance = false;
			state.vaults.amountError = null;
			state.vaults.amount = "0";
			state.vaults.amountUSD = 0;
		}));
	}


	const setActiveSection = (section: VaultSection) => {
		set(produce<Store>(state => {
			state.vaults.activeSection = section;
		}));
	};


	const setActiveVault = async ( identifier: string, section?: VaultSection, navigateToVault?: boolean) => {

		const currentChain = getChainId(config);
		const { available, } = get().vaults;
		const vault = available.find(v => v.address === identifier || v.symbol === identifier);
		section = section || globalThis.location.pathname.split("/").pop() as VaultSection || "terms-and-fees";

		if(vault?.symbol) {

			if(vault.chain.id !== currentChain) {
				try {
					await switchChain(config, { chainId: vault.chain.id, });
				}
				catch(err) {
					console.error(err);
				}
			}

			const actual = await R.getLoadedVault(get(), vault);
			const balanceBigint = await R.getDenoBalance(actual.denomination);

			const positions = await R.pennyworks.getVaultPositions(actual);
			if(_unsubActiveVault) {
				_unsubActiveVault();
			}

			_unsubActiveVault = await subscribe(`vaults.${actual.address.toLowerCase()}`, onVaultMessage);

			set(produce<Store>(state => {
				state.vaults.active = actual;
				state.vaults.participant = !!actual.transactions.length;
				state.vaults.balance = toFixedDown(Number(balanceBigint) / (10**actual.denomination.decimals), 5);

				state.vaults.kyc_required = actual.kyc_required;
				state.vaults.kyc_min_tier = actual.kyc_tier;
				state.vaults.kyb_enabled = true; //actual.kyb_enabled;
				state.vaults._displayKyc = (
					globalThis.location.hostname !== "app.bracket.fi" &&
					globalThis.location.search.toLowerCase().includes("kyc=true") &&
					actual.kyc_active
				);
				state.vaults._displayWrappables = true;
				state.vaults.activeSection = isVaultSection(section) ? section : actual.transactions.length > 0 ? "transactions" : "terms-and-fees";
			}));

			if(navigateToVault) {
				navigateTo(vault.address);
			}

			return;

		}

		throw new Error(`cannot set unknown vault identified by "${identifier}"`);
	};


	const setActiveAdminVault = async ( identifier: string, ) => {

		const {
			providing,
			onAdminVaultMessage,
		} = get().vaults;

		const vault = providing.find(v => v.address === identifier || v.symbol === identifier);
		
		if(vault?.symbol) {

			const actual = await R.getManagedVault(get(), vault);

			if(_unsubActiveAdminVault) {
				_unsubActiveAdminVault();
			}

			_unsubActiveAdminVault = await subscribe(`vaults.${actual.address.toLowerCase()}`, onAdminVaultMessage);

			set(produce<Store>(state => {
				state.vaults.activeAdminVault = actual;
				state.vaults._displayKyc = (
					globalThis.location.hostname !== "app.bracket.fi" &&
					globalThis.location.search.toLowerCase().includes("kyc=true") &&
					actual.kyc_active
				);
			}));

			return;

		}

		throw new Error(`cannot set unknown admin vault identified by "${identifier}"`);

	};


	const setInitialAmounts = () => {
		set(produce<Store>(state => {
			state.vaults.amount = "0";
			state.vaults.amountUSD = 0;
			state.vaults.hasInsufficientBalance = false;
			state.vaults.needsApproval = true;
			state.vaults.amountError = null;
		}));
	};


	const setMax = () => {

		const {
			balance,
			txnMode,
			active,
		} = get().vaults;

		if(!active) {
			throw new Error("no active vault set");
		}


		switch(txnMode) {

			case "DEPOSIT": {
				set(produce<Store>(state => {
					state.vaults.amount = balance;
				}));
				onAmountChange(balance);
				break;
			}

			case "WITHDRAW": {
				const { active_shares, } = active.exposure;
				const available_shares = floorToPrecision(floorToPrecision(active_shares), 5).toFixed(5);
				const next = Number(available_shares) < 0 ? "0" : available_shares;
				set(produce<Store>(state => {
					state.vaults.amount = next;
				}));
				onAmountChange(next);
				break;	
			}

			case "WRAP": {
				console.warn("setMax Wrap unimplemented");
				break;
			}

			case "UNWRAP": {
				console.warn("setMax Wrap unimplemented");
				break;
			}

			default: {
				throw new Error("invalid transaction mode");
			}

		}

	};


	const setTxnMode = (mode: VaultTrxnMode) => {
		set(produce<Store>(state => {
			state.vaults.txnMode = mode;
			state.vaults.transacting = false;
		}));
	};


	const toggleAutoWrap = () => {

		// we need to confirm that vault token currency is immediately rewarded before we can re-enable this
		// set(state => {
		// 	state.vaults.wrap_deposit = !state.vaults.wrap_deposit;
		// });

		return;
	};


	const updateNav = ( nav: Partial<Nav>, ) => {

		const { activeAdminVault, } = get().vaults;

		if(!activeAdminVault) {
			throw new Error("no active admin vault set");
		}
		const next = Object.assign({}, activeAdminVault.nav, nav);
		set(produce<Store>(state => {
			state.vaults.transacting = true;
		}));

		(async () => {
			const result = await R.processNavUpdate(activeAdminVault, next);
			const notification = N.dispatchNavNotification(result);

			if(notification) {
				get().notificationSlice.addNotification(notification);
			}

			await refetchActiveVault();

			set(produce<Store>(state => {
				state.vaults.transacting = false;
			}));
	
		})();
	}


	const updateVanityNav = ( nav: WEIString, ) => {
		const { activeAdminVault } = get().vaults;
		if(!activeAdminVault) {
			throw new Error("no active admin vault set");
		}
		set(produce<Store>(state => {
			state.vaults.transacting = true;
		}));

		(async () => {

			const result = await R.mgmt.processVanityNavUpdate(activeAdminVault.address, nav);

			if(result.success) {
				get().notificationSlice.addNotification(N.vanityNavUpdateSuccessNotification(result.hash));
			}
			else if(!result.success && result.rejected) {
				return;
			}
			else {
				get().notificationSlice.addNotification(N.vanityNavUpdateFailedNotification(result.error));
			}

			await refreshVaultData();

			set(produce<Store>(state => {
				state.vaults.transacting = false;
			}));
	
		})();
	}


	const withdraw = () => {

		set(produce<Store>(state => {
			state.vaults.transacting = true;
		}));

		(async () => {

			const { active, amount: shares, } = get().vaults;

			if(!active) {
				throw new Error("no active vault set");
			}
			const result = await R.withdraw({ vault: active, shares, });

			if(!result.success) {
				if(result.rejected) {
					set(produce<Store>(state => {
						state.vaults.transacting = false;
					}));
					return;
				}
			}

			const notification = N.dispatchWithdrawalNotification(result);
			if(notification) {
				get().notificationSlice.addNotification(notification);
			}

			set(produce<Store>(state => {
				state.vaults.transacting = false;
				state.vaults.amount = "0";
			}));

			_onTransacted(result.success ? "success" : "error");

		})();
	}


	const _fetchMorphoMarketData = async ( available: LoadedVaultWithPnl[], ): Promise<[ MorphoMarketDataSet, LoadedVaultWithPnlAndMorpho[], ]> => {

		const { address: walletAddress, anonymous, } = get().wallet;
		const address = !anonymous && globalThis.location.search.includes("test-morpho") ? "0xEe8BADbe45AC071f43952B9C2d145B116a8E75f4" : walletAddress;
		let next: LoadedVaultWithPnlAndMorpho[] = [];

		if(!address) {
			throw new Error("_fetchMorphoMarketData:: no wallet address set");
		}

		const morphoData = await R.morpho.getMorphoForAllVaults({ wallet: { address, }, vaults: available, });
		const engaged = Object.values(morphoData.vaults || {}).some(v => v !== null) ?? false;
		const morpho = morphoData.all ? { ready: true, engaged, position: morphoData.all, initialized: true, } : { ready: true, engaged, position: null, initialized: true, };
		for(const nv of available) {
			if(morphoData.vaults && morphoData.vaults[nv.symbol]) {
				next.push({ ...nv, morpho: { ready: true, engaged: true, position: morphoData.vaults[nv.symbol], initialized: true, } });
			}
			else {
				next.push(nv);
			}
		}

		return [ morpho, next ];

	};


	const _applyMorphoMarketData = async () => {

		try {

			set(produce<Store>(state => {
				state.vaults.available.map(async vault => {
					if(isActiveMorphoVault(vault)) {

						const {
							exposure: {
								est_annual_earnings: {
									usd: est_annual_earnings_usd,
								},
								current_value: {
									brkt: current_value_brkt,
									usd: current_value_usd
								},
							},
							meta: {
								apy: vaultApyPerc,
							},
							denomination,
							denomination: {
								decimals,
							},
							morpho,
							morpho: {
								position: M,
							}
						} = vault;

						const updated_est_annual_earnings_usd = est_annual_earnings_usd - (M.morphoBorrow.usd * ( M.morphoApy.perc / 100 ));
						vault.exposure.est_annual_earnings = {
							brkt: "0",
							usd: updated_est_annual_earnings_usd,
						};


						// if(vault.symbol === "brETH") {
						// 	const _morpho = JSON.parse(JSON.stringify(morpho));

							
						// 	console.log("brETH>>", vault.exposure.est_annual_earnings);
						// }
						// const vaultApy = vaultApyPerc / 100;
						// est_annual_earnings_usd
						// const bracket_est_annual_earnings = (BigInt(current_value_brkt) * BigInt(Math.floor(vaultApyPerc * (10**decimals))) / BigInt(10**decimals));

						// const bracket_est_annual_earnings_usd_pure = current_value_usd * vaultApyPerc;
						// const __aa = await usdToAsset(bracket_est_annual_earnings_usd_pure, vault.denomination);
						// const bracket_est_annual_earnings_usd_conv = await assetToUsd(bracket_est_annual_earnings, vault.denomination);

						// let est_annual_earnings = bracket_est_annual_earnings;
						// if(vault.morpho.position) {

						// 	const { morphoBorrow: { asset: morphoBorrowAsset, }, morphoApy: { perc: morphoApyPerc, } } = vault.morpho.position;
						// 	const morpho_est_annual_earnings = (BigInt(morphoBorrowAsset) * BigInt(Math.floor((morphoApyPerc/100) * (10**decimals))) / BigInt(10**decimals));

						// 	est_annual_earnings = est_annual_earnings + morpho_est_annual_earnings;

						// }
						// vault.exposure.est_annual_earnings = {
						// 	brkt: bracket_est_annual_earnings.toString(),
						// 	usd: 0,
						// };
					}
					return vault;
				});
			}));	

			let idx = 0;
			for(const vault of get().vaults.available) {
				const brkt = await usdToAsset(vault.exposure.est_annual_earnings.usd, vault.denomination);
				set(produce<Store>(state => {
					state.vaults.available[idx].exposure.est_annual_earnings.brkt = brkt;
				}));
				idx++;
			}

		}
		catch(err) {
			console.error("failed to apply morpho market data", err);
		}
	};


	const isActiveMorphoVault = ( vault: any ): vault is LoadedVaultWithPnl & { morpho: MorphoMarketDataSet; } => {
		if(typeof vault === "object" && !Array.isArray(vault) && vault !== null) {
			return "morpho" in vault && vault.morpho.ready && vault.morpho.engaged && vault.morpho.initialized === true;
		}
		return false;
	}


	const _applyQueryString = async () => {
		const { providing, } = get().vaults;
		if(providing.length) {
			if(globalThis.location.search.includes("aav")) {
				const parts = decodeURIComponent(globalThis.location.search).split(/[&?]/g);
				const symbol = parts.find(part => part.startsWith("aav="))?.split("=")[1];
				if(symbol) {
					await setActiveAdminVault(symbol);
				}
			}
			else {
				set(produce<Store>(state => {
					state.vaults.activeAdminVault = providing.find(v => v.symbol === "bravUSDC") || providing[0];
				}));
			}
		}
	};


	const _onTransacted = async ( status: "success" | "error", ) => {
		set(produce<Store>(state => {
			state.vaults.transacting = false;
			state.vaults.transacted = true;
			state.vaults.transactedStatus = status;
		}));
	};


	const _extractVaultSymbolFromURL = (): { vault: VaultSymbol | undefined, } => {
		const [ _, __, symbol, section, ] = globalThis.location.pathname.split("/");
		return { vault: symbol?.length ? (symbol as VaultSymbol) : undefined, section: section?.length ? (section as VaultSection) : undefined, };
	}


	const _onWalletChange =  () => {
		refetchActiveVault();
		refetchActiveAdminVault();
	}


	const onWalletMessage: RMQMessageHandler = async ( message, ) => {

		try {
			const vault_address = get().vaults.active?.address.toLowerCase();
			const admin_vault_address = get().vaults.activeAdminVault?.address.toLowerCase();
			if(message && typeof message === "object" && !Array.isArray(message) && message !== null) {
				const { type, data, } = message;
				if(typeof type === "string" && type.startsWith("BRKTWALLET:")) {
					if(data && typeof data === "object" && !Array.isArray(data) && data !== null) {
						if("vault_address" in data && typeof data.vault_address === "string") {
							if(data.vault_address.toLowerCase() === vault_address) {
								await refetchActiveVault();
							}
							if(data.vault_address.toLowerCase() === admin_vault_address) {
								await refetchActiveAdminVault();
							}
							return;
						}
					}
				}
			}
		}
		catch(err) {
			console.warn("failed to process BRKTWALLET message targetted for the current wallet", message);
			console.error(err);
		}
	}


	const _poll = async () => {

		const {
			active,
			balance,
		} = get().vaults;

		if(active) {
			const balanceBigint = await R.getDenoBalance(active.denomination);
			const nextBalance = toFixedDown(Number(balanceBigint) / (10**active.denomination.decimals), 5);
			if(nextBalance !== balance) {
				set(produce<Store>(state => {
					state.vaults.balance = nextBalance;
				}));
			}


		}
		if(_pollcnt % 6 === 0) { // every 60 seconds
			await refreshVaultData();
		}

		_pollcnt++;
	}


	const _startPoll = async (): Promise<void> => {
		if(_listener) { _clearPoll(); }
		_listener = setInterval(_poll, 10_000);
	};


	const _clearPoll = () => {
		if(_listener) {
			clearInterval(_listener);
			_listener = null;
		}
	}


	let _unsub: null | Awaited<ReturnType<typeof subscribe>> = null;
	let _unsubActiveVault: null | (() => void) = null;
	let _unsubActiveAdminVault: null | (() => void) = null;
	let _unwatchWallet: null | (() => void) = null;
	let _listener: null | NodeJS.Timeout = null;

	// place at the bottom of the call stack.
	const [ _, segment, symbol, section, ] = globalThis.location.pathname.split("/");
	const fetchProps = segment === "invest" ? { vault: symbol, section, } : {};
	setTimeout(_ => fetch(fetchProps), 0);

	return {

		_displayKyc: false,
		_displayWrappables: true,

		ready: false,
		fetching: false,

		activeSection: "terms-and-fees",
		acceptedKYCTerms: false,
		acceptedTerms: false,
		active: null as LoadedVaultWithPnl | null,
		activeAdminVault: null as ManagedVault | null,
		allowance: 0n,
		amount: "0",
		amountError: null as "Insufficient allowance" | "Insufficient balance" | null,
		amountUSD: 0,
		available: [] as LoadedVaultWithPnl[],
		balance: "0",
		exposure: R.initialExposure,
		fetchingItems: [],
		hasInsufficientBalance: false,
		kyb_enabled: false,
		kyc_min_tier: 0,
		kyc_required: false,
		morpho: { ready: false, engaged: false, position: null, initialized: false, attributes: null, },
		needsApproval: true,
		participant: false,
		providing: [] as ManagedVault[],
		transacted: false,
		transacting: false,
		transactedStatus: "idle",
		txnMode: "DEPOSIT",
		vaultNotFound: false,

		fetch,
		acceptKYCTerms,
		acceptTerms,
		approve,
		claim,
		claimBrktTvlFees,
		claimManagementFees,
		claimManagementPerformanceFees,
		claimManagementTvlFees,
		deposit,
		fetchHoldings,
		refetch,
		refetchActiveAdminVault,
		refetchActiveVault,
		refreshVaultData,
		reset,
		resetAsync,
		resetTransaction,
		setActiveSection,
		setActiveVault,
		setActiveAdminVault,
		setInitialAmounts,
		setTxnMode,
		setMax,
		toggleAutoWrap,
		updateNav,
		updateVanityNav,
		withdraw,
		cleanup,
		onAdminVaultMessage,
		navigateTo,
		onAmountChange,

	};

};
